import React, { useEffect, useRef, useState } from "react";
import "./TamGrouting.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/Tgaller1.jpeg";
import gallery2 from "../assets/gallery/Tgaller2.jpeg";
import gallery3 from "../assets/gallery/Tgaller3.jpeg";
import gallery4 from "../assets/gallery/Tgaller4.jpeg";
import gallery5 from "../assets/gallery/Tgaller5.jpeg";
import gallery6 from "../assets/gallery/Tgaller6.jpeg";
import gallery7 from "../assets/gallery/Tgaller7.jpeg";
import gallery8 from "../assets/gallery/Tgaller8.jpeg";
import overviewImg from "../assets/gallery/tg-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
];

/* ── Main TamGrouting Component ── */
const TamGrouting = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "TAM Sleeve Pipe Installation",
      desc: "Tube-à-Manchette pipes installed into pre-drilled boreholes, fitted with rubber sleeve valves for staged, repeatable grout injection.",
      icon: "⬇",
    },
    {
      number: "02",
      title: "Sleeve Port Activation",
      desc: "Double packer system isolates and activates individual sleeve ports, allowing precise, zone-specific grout delivery at depth.",
      icon: "🔧",
    },
    {
      number: "03",
      title: "Compensation Grouting",
      desc: "Controlled grout injection beneath structures to offset settlement induced by adjacent tunnelling or excavation works.",
      icon: "🏗",
    },
    {
      number: "04",
      title: "Quality work",
      desc: "Our experteised team excute the work with great quality to ensure the target is achieved improving the ground strength and achieve lower permeability.",
      icon: "💉",
    },
    {
      number: "05",
      title: "Grout Mix Design & QC",
      desc: "Laboratory-verified grout mix proportioning with on-site batching control and fluidity/bleed testing for consistent injection quality.",
      icon: "🔬",
    },
    {
      number: "06",
      title: "Monitoring & Reporting",
      desc: "Real-time pressure and volume logging with settlement monitoring, supported by detailed grouting records and as-built reports.",
      icon: "📋",
    },
  ];

  return (
    <div className="tg-page" id="#tam-grouting">
      {/* Top Banner */}
      <section className="tg-top-banner">
        <div className="tg-top-banner__grid" />
        <div className="tg-top-banner__glow" />
        <h1 className="tg-top-banner__title">
          TAM <span className="tg-text-accent">Grouting</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`tg-overview ${isVisible["overview"] ? "tg-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="tg-overview__inner">
          <div className="tg-overview__left">
            <div className="tg-section-label">
              <span className="tg-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="tg-section-title">
              PRECISION CONTROL
              <br />
              <span className="tg-text-accent">OF THE GROUND</span>
            </h2>
            <p className="tg-overview__text">
              When ground improvement and settlement control demand precision, Tube-à-Manchette
              (TAM) grouting delivers it. GSK Geotech's TAM Grouting service uses sleeved
              injection pipes to deliver grout exactly where and when it's needed — strengthening
              soil, sealing fissures, and compensating for ground movement.
            </p>
            <p className="tg-overview__text">
              Our experienced grouting team has executed TAM works across major tunnelling,
              excavation, and underpinning projects, delivering controlled, repeatable
              injection programmes with full pressure and volume documentation.
            </p>
            <div className="tg-overview__highlights">
              {[
                "Experienced TAM grouting specialists",
                "Quality work",
                "End-to-end: installation, injection, monitoring",
                "Compliant with industry & regulatory standards",
              ].map((item, i) => (
                <div key={i} className="tg-overview__highlight-item">
                  <span className="tg-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="tg-overview__right">
            <div className="tg-overview__image-block">
              <img
                src={overviewImg}
                alt="TAM Sleeve Pipe Ground Improvement Works"
                className="tg-overview__image"
              />
              <div className="tg-overview__stat-card">
                <span className="tg-overview__stat-number">25+</span>
                <span className="tg-overview__stat-text">TAM GROUTING<br />PROJECTS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`tg-methods ${isVisible["methods"] ? "tg-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="tg-methods__inner">
          <div className="tg-section-label">
            <span className="tg-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="tg-section-title">
            GROUTING
            <br />
            <span className="tg-text-accent">METHODS</span>
          </h2>
          <p className="tg-methods__subtitle">
            From sleeve pipe installation to staged injection — we deploy a full suite of
            TAM grouting techniques tailored to each project's ground conditions.
          </p>
          <div className="tg-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="tg-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="tg-method-card__number">{method.number}</div>
                <div className="tg-method-card__icon">{method.icon}</div>
                <h3 className="tg-method-card__title">{method.title}</h3>
                <p className="tg-method-card__desc">{method.desc}</p>
                <div className="tg-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`tg-process ${isVisible["process"] ? "tg-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="tg-process__inner">
          <div className="tg-section-label">
            <span className="tg-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="tg-section-title">
            OUR <span className="tg-text-accent">PROCESS</span>
          </h2>
          <div className="tg-process__steps">
            {[
              { step: "01", title: "Site Mobilisation", desc: "Grout plant setup, drilling rig positioning, safety checks and permit acquisition prior to works." },
              { step: "02", title: "TAM Pipe Installation", desc: "Sleeved pipes installed into pre-drilled boreholes and grouted in place with a weak annulus sleeve grout." },
              { step: "03", title: "Staged Injection", desc: "Double packer isolates each sleeve port for controlled, sequential grout injection at target depths." },
              { step: "04", title: "Pressure & Volume Monitoring", desc: "Real-time logging of injection pressure, volume, and flow rate with settlement instrumentation readings." },
              { step: "05", title: "Reporting", desc: "Detailed grouting records, as-built sleeve port logs, and interpretive report with performance verification." },
            ].map((item, i) => (
              <div key={i} className="tg-process__step">
                <div className="tg-process__step-num">{item.step}</div>
                <div className="tg-process__step-connector" />
                <div className="tg-process__step-content">
                  <h4 className="tg-process__step-title">{item.title}</h4>
                  <p className="tg-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`tg-projects ${isVisible["projects"] ? "tg-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="tg-projects__inner">
          <div className="tg-section-label">
            <span className="tg-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="tg-section-title">
            COMPLETED TAM
            <br />
            <span className="tg-text-accent">GROUTING PROJECTS</span>
          </h2>
          <div className="tg-projects__grid">
            {[
              { title: "Drilling Work for TAM Grouting work at T313", client: "JAE HWAN CONSTRUCTION PTE LTD" },
              { title: "Drilling Work and TAM Grouting Work at N102", client: "SSANGYONG-WAIFONG JOINT VENTURE" },
              { title: "LSS Coring and Drilling/Installation of TAM pipe at TWRP", client: "Jae Hwan Construction Pte Ltd" },
              { title: "LSS Coring and Drilling/Installation of TAM pipe at TWRP", client: "Jae Hwan Construction Pte Ltd" },
              { title: "TAM grouting Work at MH04 at Jalan Buroh for DTSS 2 Link Sewers", client: "JAE HWAN CONSTRUCTION PTE LTD" },
              { title: "TAM grouting Work at Pasir Ris Industrial Ave 1", client: "Takenaka Corporation" },
              { title: "TAM grouting Works at Selegie", client: "Be Time Engineering Pte Ltd" },
              { title: "TAM grouting Works at DTSS Link Sewer Mh11 LK11C4", client: "MK Tunnel Pte Ltd" },
              { title: "Construction of Tunnels from Elias road to Punggol P103", client: "Sindong Geological Engineering Pte Ltd" },
              { title: "TAM grouting work for DTSS2 Phase 2 LK2C1", client: "King View Engineering Pte Ltd" },
            ].map((proj, i) => (
              <div key={i} className="tg-project-card">
                <h4 className="tg-project-card__title">{proj.title}</h4>
                <p className="tg-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="tg-projects__cta">
            <a href="/projects" className="tg-btn tg-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="tg-gallery">
        <div className="tg-gallery__inner">
          <div className="tg-section-label">
            <span className="tg-section-label__line" />
            SITE PHOTOS
          </div>
          <h2 className="tg-section-title">
            PROJECT <span className="tg-text-accent">GALLERY</span>
          </h2>
          <div className="tg-gallery__masonry">
            {GALLERY_IMAGES.map((img, i) => (
              <div key={i} className="tg-gallery__item">
                <img src={img.src} alt={img.caption} className="tg-gallery__img" />
                <div className="tg-gallery__overlay">
                  <span className="tg-gallery__caption">{img.caption}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="tg-cta">
        <div className="tg-cta__inner">
          <div className="tg-cta__left">
            <p className="tg-cta__label">READY TO START?</p>
            <h2 className="tg-cta__title">
              NEED TAM GROUTING
              <br />
              <span className="tg-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="tg-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's ground improvement requirements.
            </p>
          </div>
          <div className="tg-cta__right">
            <a href="/contact-us" className="tg-btn tg-btn--primary tg-btn--large">
              CONTACT US →
            </a>
            <div className="tg-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TamGrouting;